import {
  CREATOR_CHAT,
  CREATOR_PROFILE,
  LIVE_CONFIGURATION,
  POST_CONFIGURATION,
  PUSH_CONFIGURATION
} from "../../constants/routes";
import {
  loginLinkRoutes,
  signupLinkRoutes
} from "../../constants/signupLinkRoutes";
import { Button, IconName, Logo, Text } from "@orgmym/leeloo-core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Item } from "react-stately";

import { Icon } from "../icon/Icon";
import * as styles from "./navigation-bar.css";

import { LoginLink } from "@/features/authentication/presentation/containers/login-link/LoginLink";
import { SignupLink } from "@/features/authentication/presentation/containers/signup-link/SignupLink";
import Menu from "@/shared/containers/menu/Menu";

type MenuItem = {
  label: string;
  icon: IconName;
  href?: string;
};

interface NavigationBarProps {
  menuProfile?: MenuItem[];
  withMobileNavBar?: boolean;
  withProfileMenu?: boolean;
  logged?: boolean;
}

const NavigationBar = ({
  withProfileMenu = true,
  withMobileNavBar,
  logged,
  ...rest
}: NavigationBarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const path = window.location.pathname;

  const links: {
    name: string;
    href: string;
    testId: string;
    icon:
      | "tools"
      | "statistics"
      | "message"
      | "notification"
      | "profile"
      | "settings";
  }[] = [
    {
      name: t("navbar_profile"),
      href: `${window.location.origin}/app/profile`,
      icon: "profile",
      testId: "nav-profile"
    },
    {
      name: t("studio.title"),
      href: `${window.location.origin}/app/studio`,
      icon: "tools",
      testId: "nav-tools"
    },
    {
      name: t("navbar_messages"),
      href: CREATOR_CHAT,
      icon: "message",
      testId: "nav-messages"
    },
    {
      name: t("navbar_statistics"),
      href: `${window.location.origin}/app/incomes`,
      icon: "statistics",
      testId: "nav-income"
    },
    {
      name: t("notifications_title"),
      href: `${window.location.origin}/app/notifications`,
      icon: "notification",
      testId: "nav-notifications"
    },
    {
      name: t("navbar_settings"),
      href: `${window.location.origin}/app/reglages`,
      icon: "settings",
      testId: "nav-settings"
    }
  ];

  interface MenuItem {
    label: string;
    icon: IconName;
    href: string;
  }

  const menu: MenuItem[] = [
    {
      label: "Post",
      icon: "post",
      href: `../post/${POST_CONFIGURATION}`
    },
    {
      label: "Push",
      icon: "push",
      href: `../push/${PUSH_CONFIGURATION}`
    }
  ];

  return (
    <nav
      className={styles.navigationBar({
        withMobileNavBar: !!withMobileNavBar,
        logged
      })}
      data-testid="navigation-bar"
      {...rest}
    >
      <ul className={styles.navigationList}>
        <a href={CREATOR_PROFILE} data-testid="logo-link">
          <Logo
            data-testid="logo"
            className={clsx(
              styles.logo,
              withMobileNavBar && styles.logoHideMobile
            )}
          />
        </a>
        {logged && (
          <div className={styles.navigationLinks}>
            {links.map((link, index) => (
              <li
                key={index}
                className={
                  withMobileNavBar && link?.icon === "notification"
                    ? styles.mobileHidden
                    : undefined
                }
              >
                <a
                  className={styles.linkItem}
                  href={link?.href}
                  rel="noreferrer noopenner"
                  data-testid={link?.testId}
                >
                  <Icon
                    name={link?.icon}
                    size="large"
                    data-testid={`${link?.testId}-icon`}
                  />
                  <Text
                    className={styles.desktopContent}
                    data-testid={`${link?.testId}-label`}
                  >
                    {link?.name}
                  </Text>
                </a>
              </li>
            ))}
            {withProfileMenu && (
              <Menu
                className={styles.buttonAction}
                onAction={(key) => navigate(`${key}`)}
                popoverProps={{ withUnderlay: false }}
                buttonComponent={
                  <Button leftIcon="add" className={styles.buttonAction}>
                    {t("create_new_media_button")}
                  </Button>
                }
              >
                {menu?.map((item) => (
                  <Item key={item?.href} textValue={item?.label}>
                    <Text>{item.label}</Text>
                    <Icon name={item.icon} size="large" />
                  </Item>
                ))}
              </Menu>
            )}
          </div>
        )}
        {loginLinkRoutes.includes(path) ? <LoginLink centered /> : null}
        {signupLinkRoutes.includes(path) ? (
          <SignupLink className={styles.signupLink} />
        ) : null}
      </ul>
    </nav>
  );
};

export default NavigationBar;
